/**
 * Prompt Grouping Utilities
 *
 * When quizzing on properties where multiple features share the same value,
 * we group them into a single question. For example:
 * - 85 Philippine provinces, 17 regions → 17 questions (one per region)
 * - US states by name → 50 questions (one per state)
 */

import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

/**
 * Extract string values from a property value
 * Handles strings, arrays of strings, numbers
 */
function extractStringValues(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((v) => v != null).map(String);
  }
  return [String(value)];
}

/**
 * Generate cartesian product of arrays
 * Example: [[1,2], [3,4]] → [[1,3], [1,4], [2,3], [2,4]]
 */
function cartesianProduct<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [];
  if (arrays.length === 1) return arrays[0]!.map((item) => [item]);

  const [first, ...rest] = arrays;
  const restProduct = cartesianProduct(rest);

  return first!.flatMap((item) => restProduct.map((prod) => [item, ...prod]));
}

/**
 * Count unique prompt combinations for given property keys
 *
 * This determines the actual number of quiz questions.
 * Multiple features with the same property values = one question.
 *
 * @param collection - GeoJSON feature collection
 * @param promptKeys - Property keys to quiz on
 * @returns Number of unique prompt combinations
 */
export function countUniquePrompts(
  collection: FeatureCollection<Geometry, GeoJsonProperties> | null,
  promptKeys: string[]
): number {
  if (!collection || promptKeys.length === 0) {
    return 0;
  }

  const uniquePrompts = new Set<string>();

  for (const feature of collection.features) {
    // Extract values for each prompt key
    const valueSets = promptKeys.map((key) =>
      extractStringValues(feature.properties?.[key])
    );

    // Skip features with missing values
    if (valueSets.some((set) => set.length === 0)) {
      continue;
    }

    // Generate all combinations (for multi-property prompts)
    const combinations = cartesianProduct(valueSets);

    // Add each combination as a unique prompt
    for (const combination of combinations) {
      const promptId = combination.join("||");
      uniquePrompts.add(promptId);
    }
  }

  return uniquePrompts.size;
}

/**
 * Count unique prompts after applying filters and exclusions
 *
 * @param collection - Filtered GeoJSON collection
 * @param promptKeys - Property keys to quiz on
 * @param excludedFeatureIds - Feature IDs to exclude
 * @returns Number of unique prompts in final quiz
 */
export function countFinalPrompts(
  collection: FeatureCollection<Geometry, GeoJsonProperties> | null,
  promptKeys: string[],
  excludedFeatureIds: string[]
): number {
  if (!collection || promptKeys.length === 0) {
    return 0;
  }

  // Filter out excluded features
  const filteredCollection: FeatureCollection<Geometry, GeoJsonProperties> = {
    ...collection,
    features: collection.features.filter((feature, index) => {
      const featureId = feature.id ? String(feature.id) : `feature-${index}`;
      return !excludedFeatureIds.includes(featureId);
    }),
  };

  return countUniquePrompts(filteredCollection, promptKeys);
}
